export type SocialType = 'kakao' | 'naver' | 'google' | 'apple' | 'toss';

const SOCIAL_OAUTH_URL = {
  kakao: 'https://kauth.kakao.com/oauth/authorize',
  naver: 'https://nid.naver.com/oauth2.0/authorize',
  google: 'https://accounts.google.com/o/oauth2/auth',
  apple: 'https://appleid.apple.com/auth/authorize',
  toss: 'https://oauth2.cert.toss.im/authorize',
} as const;

const base64EncodeUtf8 = (value: string) => {
  const utf8Bytes = new TextEncoder().encode(value);

  if (typeof globalThis.btoa === 'function') {
    const binaryString = String.fromCodePoint(...utf8Bytes);

    return globalThis.btoa(binaryString);
  }

  return Buffer.from(utf8Bytes).toString('base64');
};

/**
 * OAuth state를 base64로 인코딩
 * UTF-8 문자열을 안전하게 base64로 변환
 */
export const encodeOAuthState = (type: SocialType, redirectPath: string) => {
  const obj = { type, redirectPath };
  const json = JSON.stringify(obj);

  return base64EncodeUtf8(json);
};

interface CreateOAuthUrlParams {
  /** NEXT_PUBLIC_DOMAIN */
  domain: string | undefined;
  type: SocialType;
  /** NEXT_PUBLIC_{TYPE}_ID */
  clientId: string | undefined;
  scope?: string;
  /** Apple은 응답을 POST로 보내서 API Route에서 받아야 함 */
  appleApiRoutePath?: string;
  /** OAuth 응답을 핸들링할 페이지 */
  redirectPath?: string;
}

/** 인증 받기위해 들렀다 오는 소셜 로그인 URL 만들어주는 함수 */
export const createOAuthUrl = ({
  domain,
  type,
  clientId,
  scope,
  appleApiRoutePath = '/api/auth/apple',
  redirectPath = '/auth',
}: CreateOAuthUrlParams) => {
  if (!domain || !clientId) throw new Error('domain, clientId 는 필수입니다.');

  const url = SOCIAL_OAUTH_URL[type];

  const searchParams = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    state: encodeOAuthState(type, redirectPath),
  });

  if (scope) searchParams.append('scope', scope);

  switch (type) {
    case 'apple': {
      searchParams.append(
        'redirect_uri',
        `${domain.replace('https://', 'https://www.')}${appleApiRoutePath}`,
      );
      searchParams.append('response_mode', 'form_post');
      break;
    }

    default: {
      searchParams.append('redirect_uri', `${domain}${redirectPath}`);
      break;
    }
  }

  return `${url}?${searchParams.toString()}`;
};
